import * as React from "react";
import { Button, Hint } from "react-ui";

import { COLGROUP_BUTTON_CLICK } from "../../metricsAction";
import { LayoutEvents } from "../../Internals/LayoutEvents/LayoutEvents";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { emptyRef } from "../../TypingUtils";
import { useTourStepRef } from "../../Internals/TourManagement/UseTourStepRef";
import { colgroupButtonTourStep } from "../../FormParts/Tour/Steps/ColgroupButton";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { Icon } from "../../Icons/Icon";

import { ButtonText, CaretLeftFull, CaretRightFull, CollapsedSpan, Container, Root } from "./ColgroupButton.styles";
import { ColgroupButtonTestPageObject } from "./ColgroupButtonTestPageObject";

export interface ColgroupButtonProps {
    bindingPath: AbsoluteModelPath;
    collapsed: boolean;
    onCollapsedChange: (nextCollapsed: boolean) => void;
    children: React.ReactChild;
    hiddenColgroup: string;
    hideText: string;
    isLast?: boolean;
    showText: string;
}

export function ColgroupButton(props: ColgroupButtonProps): React.ReactElement {
    const hint = React.useRef<Hint>(emptyRef);
    const tourRef = useTourStepRef(() => ({
        ...colgroupButtonTourStep,
        options: {
            path: props.bindingPath.toLegacyPath(),
            groupKey: props.hiddenColgroup,
        },
    }));
    const track = useMetrics();

    React.useEffect(() => {
        if (props.isLast || props.collapsed) {
            LayoutEvents.emit();
        }
    }, [props.isLast, props.collapsed]);

    const handleClick = () => {
        track(COLGROUP_BUTTON_CLICK, { colgroupState: props.collapsed ? "hide" : "show", group: props.hiddenColgroup });
        props.onCollapsedChange(!props.collapsed);

        // @ts-ignore В Hint баг с появлением подсказки после изменения opened на false
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        clearTimeout(hint.timer);
        if (!props.collapsed) {
            if (hint.current != undefined) {
                hint.current.setState({ opened: false });
            }
        }
    };

    useTestPageObject(() => ColgroupButtonTestPageObject(handleClick, props.collapsed), [props.collapsed]);
    const ArrowShapeTriangleSize = 18;
    function renderCollapsed(): React.ReactElement {
        return (
            <div>
                <CollapsedSpan>
                    <Icon name="ArrowCollapseTrianglesHOpenIcon" weight="light" size={22} />
                </CollapsedSpan>
            </div>
        );
    }

    function renderOpened(): React.ReactElement {
        return (
            <ButtonText>
                <CaretLeftFull>
                    <Icon name="ArrowShapeTriangleARightIcon" size={ArrowShapeTriangleSize} weight="light" />
                </CaretLeftFull>
                {props.children}
                <CaretRightFull>
                    <Icon name="ArrowShapeTriangleALeftIcon" size={ArrowShapeTriangleSize} weight="light" />
                </CaretRightFull>
            </ButtonText>
        );
    }

    return (
        <Root last={props.isLast ?? false} collapsed={props.collapsed} ref={tourRef}>
            <Hint ref={hint} text={props.collapsed ? props.hideText : props.showText}>
                <Container>
                    <Button width={props.collapsed ? 14 : "auto"} onClick={handleClick} style={{ padding: 0 }}>
                        {props.collapsed ? renderCollapsed() : renderOpened()}
                    </Button>
                </Container>
            </Hint>
        </Root>
    );
}
